// api/folders.js
import { supabase } from '../supabaseClient';
import { debug } from '../helpers/debug';
import { sanitizeInputByType, sanitizeStructuredInput } from '../helpers/formatHelpers';

// --------------------------------------------------------------
// Get all folders in a project
// --------------------------------------------------------------
export async function apiGetFolders(projectId) {
  const { data, error } = await supabase
    .from('project_folders')
    .select('*')
    .eq('project_id', projectId)
    .order('name', { ascending: true });

  if (error) throw error;
  return data;
}

// --------------------------------------------------------------
// Create a new folder
// --------------------------------------------------------------
export async function apiCreateFolder(projectId, name, parentId = null) {
  const safeName = sanitizeInputByType(name, 'title', { maxLength: 180 }) || 'New Folder';
  const { data, error } = await supabase.rpc('create_folder', {
    p_project_id: projectId,
    p_name: safeName,
    p_parent_id: parentId ?? null
  });

  if (error) throw error;
  return data;
}

// --------------------------------------------------------------
// Delete a folder
// --------------------------------------------------------------
export async function apiDeleteFolder(folderId) {
  try {
    const { data, error } = await supabase.rpc('delete_folder', {
      p_folder_id: folderId
    });

    if (error) throw error;
    return data;
  } catch (err) {
    debug('[apiDeleteFolder] error:', { type: 'error' }, err);
    return null;
  }
}

// --------------------------------------------------------------
// Move a folder
// --------------------------------------------------------------
export async function apiMoveFolder(folderId, newParentId) {
  try {
    const { data, error } = await supabase.rpc('move_folder', {
      p_folder_id: folderId,
      p_parent_id: newParentId ?? null
    });

    if (error) throw error;
    return data;
  } catch (err) {
    debug('[apiMoveFolder] error:', { type: 'error' }, err);
    return null;
  }
}

// --------------------------------------------------------------
// Save folder updates (generic update)
// --------------------------------------------------------------
export async function apiSaveFolder(folderId, updates) {
  try {
    const safeUpdates = sanitizeStructuredInput(updates, { maxDepth: 8, maxStringLength: 2000 });
    const { data, error } = await supabase.rpc('update_folder', {
      p_folder_id: folderId,
      p_updates: safeUpdates
    });

    if (error) throw error;
    return data;
  } catch (err) {
    debug('[apiSaveFolder] error:', { type: 'error' }, err);
    return null;
  }
}

// --------------------------------------------------------------
// Rename folder
// --------------------------------------------------------------
export async function apiRenameFolder(folderId, newName) {
  try {
    const safeName = sanitizeInputByType(newName, 'title', { maxLength: 180 }) || 'Untitled';
    const { data, error } = await supabase.rpc('rename_folder', {
      p_folder_id: folderId,
      p_name: safeName
    });

    if (error) throw error;
    return data;
  } catch (err) {
    debug('[apiRenameFolder] error:', { type: 'error' }, err);
    return null;
  }
}
